import cx from 'clsx';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navigation, Pagination, Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as DeleteImageIcon } from '@assets/svg/ic-delete-image.svg';
import { ReactComponent as SwiperArrowIcon } from '@assets/svg/ic-swiper-arrow.svg';

import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import { useFileManagerUploadFileMutation } from '@services/useFileManagerUploadFileMutation';

import { useModalActions } from '@store/useModalStore';

import Dropzone from '../AttachFilesModal/components/Dropzone/Dropzone';
import ModalHeader from '../ModalHeader/ModalHeader';
import ControlPanel from './components/ControlPanel/ControlPanel';
import CurrentImage from './components/CurrentImage/CurrentImage';
import DropzoneStep from './components/DropzoneStep/DropzoneStep';
import type { GalleryPhotoType, IFile, IGalleryPhoto } from './constants/sensorGalleryType';
import { getInstallationPointIds } from './helpers/getInstallationPointIds';
import { getIsMainAssetExist } from './helpers/getIsMainAssetExist';
import getNameplatesWithoutInstallationPoints from './helpers/getNameplatesWithoutInstallationPoints';
import { useDeleteEquipmentImageMutation } from './services/useDeleteEquipmentImageMutation';
import { useSaveEquipmentImagesMutation } from './services/useSaveEquipmentImagesMutation';
import { useUpdateEquipmentImagesListMutation } from './services/useUpdateEquipmentImagesListMutation';
import style from './style.module.scss';

interface IProps {
  images: IGalleryPhoto[];
  installationPoints: IInstallationPoint[];
  equipmentName: string;
}

const acceptFiles = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
};

export const errorMessages = {
  noErrorMessage: '',
  noMainAssetPhoto: 'Please select a Main Asset Photo before proceeding.',
  noUploadedPhotos: 'Please upload new photos.',
  nameplateWithoutInstapoints: 'Please assign installation points related to the nameplate.',
};

const EditSensorGalleryModal = ({ images = [], installationPoints, equipmentName }: IProps) => {
  const { t } = useTranslation();
  const { equipmentId, customerId } = useParams();
  const { setSecondaryModal, setModalClose } = useModalActions();
  const swiperRef = useRef<SwiperType | null>();

  const { mutate: updateEquipmentImagesListMutate } = useUpdateEquipmentImagesListMutation();
  const { mutate: deleteEquipmentImagesMutate } = useDeleteEquipmentImageMutation();
  const { mutate: saveEquipmentImagesMutate } = useSaveEquipmentImagesMutation();

  const [localFiles, setLocalFiles] = useState<IGalleryPhoto[]>([]);
  const [indexPhoto, setIndexPhoto] = useState<number>(0);
  const [imagesState, setImagesState] = useState<{
    images: IGalleryPhoto[];
    uploadedImages: IGalleryPhoto[];
  }>({
    images: images,
    uploadedImages: [],
  });
  const [isNewPhoto, setIsNewPhoto] = useState<boolean>(!images.length);
  const [errorMessage, setErrorMessage] = useState<keyof typeof errorMessages>('noErrorMessage');
  const [isNavButtons, setNavButtons] = useState<boolean>(false);
  const [deletedPhotosIds, setDeletedPhotosIds] = useState<(number | string)[]>([]);

  const isMainPhotoExist = getIsMainAssetExist(imagesState, deletedPhotosIds);
  const isDropzone = !imagesState.uploadedImages.length && !imagesState.images.length;

  const slides = isNewPhoto ? imagesState.uploadedImages : imagesState.images;

  const isCurrentPhotoAllChecked =
    !!slides.length && installationPoints.length === slides[indexPhoto].installationPoint?.length;
  const imageType = isNewPhoto ? 'uploadedImages' : 'images';

  const onUploadPhotos = (acceptedFiles?: IFile[]) => {
    if (localFiles.length) {
      localFiles.forEach((file: any) => {
        const formData = new FormData();
        formData.append('file', file);

        fileManagerUploadFileMutation(
          { formData, customerId: customerId },

          {
            onSuccess: () => {
              setLocalFiles([]);
              setErrorMessage('noErrorMessage');
            },
          },
        );
      });
    } else if (acceptedFiles?.length) {
      acceptedFiles.forEach((file: any) => {
        const formData = new FormData();
        formData.append('file', file);

        fileManagerUploadFileMutation({ formData, customerId: customerId });
      });
    } else {
      setErrorMessage('noUploadedPhotos');
    }
  };

  const handleDeleteButton = (id: number | string, imageType: GalleryPhotoType) => {
    const isDeleted = deletedPhotosIds.includes(id);

    if (isDeleted) {
      setDeletedPhotosIds((prev) => prev.filter((photoId) => photoId !== id));
    } else {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          onConfirm: () => setDeletedPhotosIds((prev) => [...prev, id]),
          bodyTitle:
            imageType === 'primaryImage'
              ? t('Are you sure you want to delete this photo?')
              : t('Are you sure you want to delete the photo?'),
          bodyContent:
            imageType === 'primaryImage'
              ? t(
                  'It is currently set as the Main Asset photo and will also be removed from all associated installation points.',
                )
              : undefined,
        },
      });
    }
  };

  const onFileManagerUploadFileMutateSuccess = (data: { file: IFile }) => {
    const { url, name } = data.file;
    const currentFile = {
      id: uuidv4(),
      equipmentId: equipmentId,
      name: name,
      url: url,
      imageType: 'equipmentImage' as GalleryPhotoType,
      installationPoint: [],
      nameplateId: null,
    };

    setImagesState((prev) => {
      const newState = {
        ...prev,
        uploadedImages: [currentFile, ...prev.uploadedImages],
      };
      return newState;
    });
  };

  const { mutate: fileManagerUploadFileMutation } = useFileManagerUploadFileMutation({
    onSuccess: onFileManagerUploadFileMutateSuccess,
  });

  const handleSave = () => {
    if (!isMainPhotoExist) {
      setErrorMessage('noMainAssetPhoto');
      return;
    }
    const nameplatesWithoutInstapoint = getNameplatesWithoutInstallationPoints(imagesState.images);
    const uploadedNameplatesWithoutInstapoint = getNameplatesWithoutInstallationPoints(
      imagesState.uploadedImages,
    );
    if (nameplatesWithoutInstapoint.length) {
      const slideIndex = imagesState.images.findIndex(
        (slide) => slide.id === nameplatesWithoutInstapoint[0].id,
      );
      setIsNewPhoto(false);
      setIndexPhoto(slideIndex);
      setErrorMessage('nameplateWithoutInstapoints');
      return;
    }
    if (uploadedNameplatesWithoutInstapoint.length) {
      const slideIndex = imagesState.images.findIndex(
        (slide) => slide.id === uploadedNameplatesWithoutInstapoint[0].id,
      );
      setIsNewPhoto(true);
      setIndexPhoto(slideIndex);
      setErrorMessage('nameplateWithoutInstapoints');
      return;
    }

    if (imagesState.images.length) {
      const updatedImages = imagesState.images.map((img: IGalleryPhoto) => {
        const isAllChecked = installationPoints.length === img.installationPoint?.length;
        return {
          id: img.id,
          imageType: img.imageType,
          installationPointIds: getInstallationPointIds(img.installationPoint, img.imageType, isAllChecked),
        };
      });
      imagesState.images.forEach((img) => {
        if (deletedPhotosIds.includes(img.id)) {
          deleteEquipmentImagesMutate({
            equipment_Id: equipmentId,
            id: img.id,
            type: img.imageType,
            nameplateId: img.nameplateId ? img.nameplateId : undefined,
          });
        }
      });
      !!imagesState.images.length &&
        updateEquipmentImagesListMutate(
          {
            equipment_Id: equipmentId,
            data: updatedImages,
          },
          { onSuccess: () => showToast({ variant: 'success', message: t('Your changes have been saved.') }) },
        );
    }

    if (imagesState.uploadedImages.length) {
      const uploadedImages = imagesState.uploadedImages
        .filter((img: IGalleryPhoto) => !deletedPhotosIds.includes(img.id))
        .map((img: IGalleryPhoto) => {
          const isAllChecked = installationPoints.length === img.installationPoint?.length;
          return {
            url: img.url,
            imageType: img.imageType,
            installationPointIds: getInstallationPointIds(img.installationPoint, img.imageType, isAllChecked),
          };
        });

      uploadedImages.length &&
        saveEquipmentImagesMutate(
          {
            equipment_Id: equipmentId,
            data: uploadedImages,
          },
          {
            onSuccess: () =>
              !imagesState.images.length &&
              showToast({ variant: 'success', message: t('Your changes have been saved.') }),
          },
        );
    }
    setModalClose();
  };

  if (isDropzone) {
    return (
      <DropzoneStep
        localFiles={localFiles}
        onUploadPhotos={onUploadPhotos}
        setLocalFiles={setLocalFiles}
        errorMessage={errorMessages[errorMessage]}
      />
    );
  }

  return (
    <div className={style.editGalleryModal}>
      <ModalHeader title={t('Photo management')} />
      <div className={style.modalInfoLine}>
        {t('Please select a photo type and installation point for non-Main photos')}
      </div>
      <div className={style.modalBody}>
        <div className={style.upperBodyModal}>
          {(imagesState.images[indexPhoto] || imagesState.uploadedImages[indexPhoto]) && (
            <CurrentImage
              currentImage={slides[indexPhoto]}
              equipmentName={equipmentName}
              isAllChecked={isCurrentPhotoAllChecked}
            />
          )}
          <ControlPanel
            currentSlide={slides[indexPhoto]}
            imageType={imageType}
            indexPhoto={indexPhoto}
            installationPoints={installationPoints}
            equipmentName={equipmentName}
            isMainPhotoExist={isMainPhotoExist}
            setImageState={setImagesState}
            setErrorMessage={setErrorMessage}
          />
        </div>
        <div className={style.lowerBodyModal}>
          <Dropzone
            className={style.addPhotoSlide}
            typeOfFiles={acceptFiles}
            variant="photoBtn"
            currentFiles={[]}
            onDrop={onUploadPhotos}
          />
          <Swiper
            className={cx(style.gallerySwiper, {
              [style.twoBulletPagination]:
                slides.length - (swiperRef.current?.params.slidesPerView as number) === 1,
              [style.threeBulletPagination]:
                slides.length - (swiperRef.current?.params.slidesPerView as number) === 2,
              [style.fourBulletPagination]:
                slides.length - (swiperRef.current?.params.slidesPerView as number) === 3,
              [style.fiveBulletPagination]:
                slides.length - (swiperRef.current?.params.slidesPerView as number) === 4,
              [style.dynamicBullets]: slides.length - (swiperRef.current?.params.slidesPerView as number) > 4,
            })}
            modules={[Pagination, Navigation]}
            spaceBetween={10}
            slidesPerView={4}
            breakpoints={{
              1200: {
                slidesPerView: 5,
                spaceBetween: 20,
              },
              800: {
                slidesPerView: 4,
                spaceBetween: 20,
              },
            }}
            onSwiper={(swiper) => {
              setNavButtons(swiper.slides.length > (swiper.params.slidesPerView as number));
            }}
            onSlidesGridLengthChange={(swiper) =>
              setNavButtons(swiper.slides.length > (swiper.params.slidesPerView as number))
            }
            pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 4 }}
            onBeforeInit={(swiper) => (swiperRef.current = swiper)}
          >
            {imagesState.uploadedImages.map((img, index) => (
              <SwiperSlide
                className={cx(style.slide, {
                  [style.currentSlide]: isNewPhoto && index === indexPhoto,
                })}
                onClick={() => {
                  setIsNewPhoto(true);
                  setIndexPhoto(index);
                }}
                key={img.id}
              >
                <img src={img.url} alt={t('photo')} className={style.galleryImage} />
                {deletedPhotosIds.find((value) => value === img.id) ? (
                  <div
                    className={style.recoverImageBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteButton(img.id, img.imageType);
                    }}
                  >
                    {t('Recover')}
                  </div>
                ) : (
                  <DeleteImageIcon
                    className={style.deleteImageIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteButton(img.id, img.imageType);
                    }}
                  />
                )}
              </SwiperSlide>
            ))}
            {imagesState.images.map((img, index) => (
              <SwiperSlide
                className={cx(style.slide, {
                  [style.currentSlide]: !isNewPhoto && index === indexPhoto,
                })}
                onClick={() => {
                  setIsNewPhoto(false);
                  setIndexPhoto(index);
                }}
                key={img.id}
              >
                <img src={img.url} alt={t('photo')} className={style.galleryImage} />
                {deletedPhotosIds.find((value) => value === img.id) ? (
                  <div
                    className={style.recoverImageBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteButton(img.id, img.imageType);
                    }}
                  >
                    {t('Recover')}
                  </div>
                ) : (
                  <DeleteImageIcon
                    className={style.deleteImageIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteButton(img.id, img.imageType);
                    }}
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          {isNavButtons && (
            <>
              <button
                className={cx(style.navBtn, style.prevBtn)}
                onClick={() => swiperRef.current?.slidePrev()}
              >
                <SwiperArrowIcon className={style.prevBtnIcon} />
              </button>
              <button
                className={cx(style.navBtn, style.nextBtn)}
                onClick={() => swiperRef.current?.slideNext()}
              >
                <SwiperArrowIcon className={style.nextBtnIcon} />
              </button>
            </>
          )}
        </div>
        <div className={style.errorMessage}>
          <span>{t(errorMessages[errorMessage])}</span>
        </div>
        <div className={style.modalFooter}>
          <Button
            onClick={setModalClose}
            type="secondary"
            text={t('Cancel')}
            className={style.galleryButton}
          />
          <Button onClick={handleSave} type="primary" text={t('Save')} className={style.galleryButton} />
        </div>
      </div>
    </div>
  );
};

export default EditSensorGalleryModal;
